/* eslint-disable arrow-body-style */
import http from '@/utils/http';
import { handleSensorTowerValueFilter } from '@intelligence/utils/topchartsUtils';
import { Relation } from '@intelligence/components/GenreSelect/enums';
import { SENSOR_TOWER_TAG_PRODUCT_MODEL } from '@/pages/intelligence/components/Filter/FilterBase/_config';

import type { IDateType } from '@/pages/intelligence/types/common';
import type {
  IStoreRankTopFetchParams,
  IFetchStoreRankTopsRes,
  IMobileStoreRankTopGlobalCompareFetchParams,
  IMobileStoreRankTopGlobalCompareRank,
  IStoreRankMoreCoreFetchParams,
  IStoreRankMoreCoreRank,
  IStoreRankMoreCoreTrend,
  IStoreRankDetailsDataFetchParams,
  IStoreRankDetailsResData,
} from '@/pages/intelligence/types/storeRank';

/**
 * 将 appmagic_genre 数据转换为 app_magic 接口入参格式
 * 格式: { relation: 'or', tags: [{ name: 'xxx', children: [...] }] }
 */
const formatAppMagicGenres = (list?: any[] | null): { name: string; children: any[]; }[] => {
  if (!Array.isArray(list) || list.length === 0) {
    return [];
  }
  return list.map((item) => {
    const { value, children } = item || {};
    return { name: value || '', children: formatAppMagicGenres(children) };
  });
};

/**
 * 将 sensortower_tag_filter 格式转换为 { name, children } 格式
 * 输入格式: [{ tag_name: 'Product Model', Tags: [{ tag_name: 'Casual' }] }]
 * 输出格式: [{ name: 'Product Model', children: [{ name: 'Casual', children: [] }] }]
 */
const formatSensorTowerTags = (list?: any[] | null): { name: string; children: any[]; }[] => {
  if (!Array.isArray(list) || list.length === 0) {
    return [];
  }
  return list.map((item) => {
    const { tag_name: tagName, Tags: tags } = item || {};
    return {
      name: tagName || '',
      children: Array.isArray(tags)
        ? tags.map((tag: any) => ({ name: tag?.tag_name || '', children: [] }))
        : [],
    };
  });
};

/**
 * 统一处理 filter 参数，构建后端需要的 filter 对象
 */
const processFilterParams = (params: IStoreRankTopFetchParams): any => {
  const processedParams: any = { ...params };

  // 处理品类数据
  const appmagicGenre = (params as any).appmagic_genre || [];
  const appannieGenre = (params as any).appannie_genre || [];
  const sensortowerGenre = (params as any).sensortower_genre || [];
  const ieggGenre = (params as any).iegg_genre || [];

  // 处理 Product Model (Sensor Tower Tags)
  const productModelFilter = (params as any)[SENSOR_TOWER_TAG_PRODUCT_MODEL];
  const sensorTowerTagParams = productModelFilter?.value?.length > 0
    ? handleSensorTowerValueFilter({ [SENSOR_TOWER_TAG_PRODUCT_MODEL]: productModelFilter })
    : { sensortower_tag_filter: [] };
  const formattedTags = formatSensorTowerTags(sensorTowerTagParams?.sensortower_tag_filter);

  // 构建 filter 对象
  const filterObj: any = {
    appannie_genre: appannieGenre,
    sensortower_genre: sensortowerGenre,
    iegg_genre: ieggGenre,
  };

  // 处理 App Magic 品类
  if (appmagicGenre.length > 0) {
    filterObj.app_magic = {
      relation: Relation.Or,
      tags: formatAppMagicGenres(appmagicGenre),
    };
  }

  // 处理 Product Model tags
  if (formattedTags.length > 0) {
    filterObj.tags = formattedTags;
  }

  // 只有当 filter 对象有实际内容时才添加
  const hasFilterContent =
    appannieGenre.length > 0 ||
    sensortowerGenre.length > 0 ||
    ieggGenre.length > 0 ||
    appmagicGenre.length > 0 ||
    formattedTags.length > 0;

  if (hasFilterContent) {
    processedParams.filter = filterObj;
  }

  return processedParams;
};

/**
 * TopCharts/MobileGames/store ranks/All TopCharts 页面，获取榜单列表
 * TopCharts/PCGames/store ranks页面，获取榜单列表
 * TopCharts/ConsoleGames/store ranks页面，获取榜单列表
 * @param params
 * @returns
 */
export const fetchStoreRankTops = (
  params: IStoreRankTopFetchParams,
): Promise<IFetchStoreRankTopsRes> => {
  // 统一处理 filter 参数
  const processedParams = processFilterParams(params);
  // @ts-ignore
  return http({
    url: '/api/v1/intelligence_pc/getTopChartsStoreRank',
    method: 'post',
    data: processedParams,
    rawData: true,
  });
};

export const fetchStoreRankTopsMobile = (
  params: IStoreRankTopFetchParams,
): Promise<IFetchStoreRankTopsRes> => {
  // 统一处理 filter 参数
  const processedParams = processFilterParams(params);
  // @ts-ignore
  return http({
    url: '/api/v1/intelligence_pc/getTopChartsStoreRankV2',
    method: 'post',
    data: processedParams,
    rawData: true,
  });
};

/**
 * 统一处理 Global Compare 页面的 filter 参数，构建后端需要的 filter 对象
 */
const processGlobalCompareFilterParams = (
  params: IMobileStoreRankTopGlobalCompareFetchParams,
): any => {
  const processedParams: any = { ...params };

  // 处理品类数据
  const appmagicGenre = (params as any).appmagic_genre || [];
  const appannieGenre = (params as any).appannie_genre || [];
  const sensortowerGenre = (params as any).sensortower_genre || [];
  const ieggGenre = (params as any).iegg_genre || [];

  // 处理 Product Model (Sensor Tower Tags)
  const productModelFilter = (params as any)[SENSOR_TOWER_TAG_PRODUCT_MODEL];
  const sensorTowerTagParams = productModelFilter?.value?.length > 0
    ? handleSensorTowerValueFilter({ [SENSOR_TOWER_TAG_PRODUCT_MODEL]: productModelFilter })
    : { sensortower_tag_filter: [] };
  const formattedTags = formatSensorTowerTags(sensorTowerTagParams?.sensortower_tag_filter);

  // 构建 filter 对象
  const filterObj: any = {
    appannie_genre: appannieGenre,
    sensortower_genre: sensortowerGenre,
    iegg_genre: ieggGenre,
  };

  // 处理 App Magic 品类
  if (appmagicGenre.length > 0) {
    filterObj.app_magic = {
      relation: Relation.Or,
      tags: formatAppMagicGenres(appmagicGenre),
    };
  }

  // 处理 Product Model tags
  if (formattedTags.length > 0) {
    filterObj.tags = formattedTags;
  }

  // 只有当 filter 对象有实际内容时才添加
  const hasFilterContent =
    appannieGenre.length > 0 ||
    sensortowerGenre.length > 0 ||
    ieggGenre.length > 0 ||
    appmagicGenre.length > 0 ||
    formattedTags.length > 0;

  if (hasFilterContent) {
    processedParams.filter = filterObj;
  }

  return processedParams;
};

// TopCharts/MobileGames/store ranks/Global Compare 页面，获取国家游戏榜单列表
export const fetchMobileStoreRankTopGlobalCompare = (
  params: IMobileStoreRankTopGlobalCompareFetchParams,
): Promise<{
  data: {
    total: number;
    rank_list: IMobileStoreRankTopGlobalCompareRank[];
  };
  req: {
    date: string;
    [key: string]: any;
  };
}> => {
  // 统一处理 filter 参数
  const processedParams = processGlobalCompareFilterParams(params);
  // @ts-ignore
  return http({
    url: '/api/v1/intelligence_pc/getTopChartsGlobalCompareV2',
    method: 'post',
    data: processedParams,
    rawData: true,
  });
};

/**
 * 统一处理 Store Rank More 页面的 filter 参数，构建后端需要的 filter 对象
 */
const processStoreRankMoreFilterParams = (
  params: IStoreRankMoreCoreFetchParams,
): any => {
  const { start_release_time: startReleaseTime, end_release_time: endReleaseTime, ...processedParams } = params;

  // 处理品类数据
  const appmagicGenre = (params as any).appmagic_genre || [];
  const appannieGenre = (params as any).appannie_genre || [];
  const sensortowerGenre = (params as any).sensortower_genre || [];
  const ieggGenre = (params as any).iegg_genre || [];

  // 处理 Product Model (Sensor Tower Tags)
  const productModelFilter = (params as any)[SENSOR_TOWER_TAG_PRODUCT_MODEL];
  const sensorTowerTagParams = productModelFilter?.value?.length > 0
    ? handleSensorTowerValueFilter({ [SENSOR_TOWER_TAG_PRODUCT_MODEL]: productModelFilter })
    : { sensortower_tag_filter: [] };
  const formattedTags = formatSensorTowerTags(sensorTowerTagParams?.sensortower_tag_filter);

  // 构建 filter 对象
  const filterObj: any = {
    appannie_genre: appannieGenre,
    sensortower_genre: sensortowerGenre,
    iegg_genre: ieggGenre,
    start_release_date: startReleaseTime,
    end_release_date: endReleaseTime,
  };

  // 处理 App Magic 品类
  if (appmagicGenre.length > 0) {
    filterObj.app_magic = {
      relation: Relation.Or,
      tags: formatAppMagicGenres(appmagicGenre),
    };
  }

  // 处理 Product Model tags
  if (formattedTags.length > 0) {
    filterObj.tags = formattedTags;
  }

  // 只有当 filter 对象有实际内容时才添加
  const hasFilterContent =
    appannieGenre.length > 0 ||
    sensortowerGenre.length > 0 ||
    ieggGenre.length > 0 ||
    appmagicGenre.length > 0 ||
    formattedTags.length > 0 ||
    startReleaseTime ||
    endReleaseTime;

  if (hasFilterContent) {
    processedParams.filter = filterObj;
  }

  return processedParams;
};

/**
 * Mobile、PC、Console榜单下钻页 榜单数据接口
 */
export const fetchStoreRankMoreRanks = (
  params: IStoreRankMoreCoreFetchParams,
  config?: { [key: string]: any; },
): Promise<{
  data: {
    metric_list: string[];
    list: IStoreRankMoreCoreRank[];
    total: number;
  };
  req: {
    granularity: IDateType;
    start_date: string;
    end_date: string;
    order_source: string;
    order_metric: string;
    order: 'desc' | 'asc';
    ratio_type: 0 | 1;
  };
  ext?: {
    entity_info?: {
      [key: string]: {
        ext_json?: string;
        major_category?: string;
        mid_category?: string;
        sensortower_genre?: string;
        sensortower_sub_genre?: string;
        sub_category?: string;
        super_genre?: string;
      };
    };
  };
}> => {
  const { start_release_time: startReleaseTime, end_release_time: endReleaseTime, ...restParams } = params as any;

  // @ts-ignore
  return http({
    ...config,
    url: '/api/v1/intelligence_pc/getNewStoreRankDetailedList',
    method: 'post',
    data: {
      ...restParams,
      filter: {
        start_release_date: startReleaseTime,
        end_release_date: endReleaseTime,
      },
      use_custom_fields: ['super_genre', 'major_category', 'mid_category', 'sub_category']
    },
    rawData: true,
  });
};

export const fetchStoreRankMoreRanksMobile = (
  params: IStoreRankMoreCoreFetchParams,
  config?: { [key: string]: any; },
): Promise<{
  data: {
    metric_list: string[];
    list: IStoreRankMoreCoreRank[];
    total: number;
  };
  req: {
    granularity: IDateType;
    start_date: string;
    end_date: string;
    order_source: string;
    order_metric: string;
    order: 'desc' | 'asc';
    ratio_type: 0 | 1;
  };
  ext?: {
    entity_info?: {
      [key: string]: {
        ext_json?: string;
        major_category?: string;
        mid_category?: string;
        sensortower_genre?: string;
        sensortower_sub_genre?: string;
        sub_category?: string;
        super_genre?: string;
      };
    };
  };
}> => {
  // 统一处理 filter 参数
  const processedParams = processStoreRankMoreFilterParams(params);
  
  // 如果 order_metric 是 'rank' 并且是多国家（regions 数组长度 > 1），则将 order_metric 设置为空字符串
  if (processedParams.order_metric === 'rank' && Array.isArray(processedParams.regions) && processedParams.regions.length > 1) {
    processedParams.order_metric = '';
    processedParams.order_source = processedParams.topchart_name;
  }
  
  // @ts-ignore
  return http({
    ...config,
    url: '/api/v1/intelligence_pc/getNewStoreRankDetailedListV2',
    method: 'post',
    data: {
      ...processedParams,
      use_custom_fields: ['super_genre', 'major_category', 'mid_category', 'sub_category']
    },
    rawData: true,
  });
};

/**
 * Mobile、PC、Console榜单下钻页 榜单折线图数据接口
 */
export const fetchStoreRankMoreTrends = (
  params: IStoreRankMoreCoreFetchParams,
): Promise<{
  data: {
    time_list: {
      [key: string]: IStoreRankMoreCoreTrend[];
    };
  };
  req: {
    granularity: IDateType;
    start_date: string;
    end_date: string;
  };
}> => {
  const { start_release_time: startReleaseTime, end_release_time: endReleaseTime, ...restParams } = params as any;

  // @ts-ignore
  return http({
    url: '/api/v1/intelligence_pc/getNewStoreRankDetailedTrend',
    method: 'post',
    data: {
      ...restParams,
      filter: {
        start_release_date: startReleaseTime,
        end_release_date: endReleaseTime,
      },
    },
    rawData: true,
  });
};

// Mobile、PC、Console游戏详情页store rank页签 数据（新接口，mobile、pc、console三端统一）
export async function fetchStoreRanksDetailsData(
  params: IStoreRankDetailsDataFetchParams,
): Promise<{
    data: IStoreRankDetailsResData[];
    req: IStoreRankDetailsDataFetchParams;
  }> {
  return http({
    url: '/api/v1/intelligence_pc/detail/store_ranks',
    method: 'post',
    data: params,
    rawData: true,
  }) as any;
}

// mobile游戏详情页Store Rank 页签Rank Trend 标签宽表接口
export async function fetchStoreRanksDetailsRankData(
  params: IStoreRankDetailsDataFetchParams,
): Promise<{
    data: IStoreRankDetailsResData[];
    req: IStoreRankDetailsDataFetchParams;
  }> {
  return http({
    url: '/api/v1/intelligence_pc/mobile/detail/store_rank',
    method: 'post',
    data: params,
    rawData: true,
  }) as any;
}

// mobile游戏详情页Store Rank 页签Rank Trend 标签折线图接口
export async function fetchStoreRanksDetailsTrendData(
  params: IStoreRankDetailsDataFetchParams,
): Promise<{
    data: IStoreRankDetailsResData[];
    req: IStoreRankDetailsDataFetchParams;
  }> {
  return http({
    url: '/api/v1/intelligence_pc/mobile/detail/store_rank_trend',
    method: 'post',
    data: params,
    rawData: true,
  }) as any;
}

// Mobile游戏详情页store rank页签 获取子表格数据，即各个国家的排名数据
export async function fetchMobileStoreRankDetailsCountriesRanks(params: {
  entity_type: string;
  app_ids: string[];
  source: string[];
  region_type: string;
  regions: string[];
  end_date: string;
}): Promise<
  {
    market: string;
    rank: {
      name: string;
      rank: number | null;
    }[];
  }[]
  > {
  // @ts-ignore
  return http({
    url: '/api/v1/intelligence_pc/detail/mobile/store_rank/market',
    method: 'post',
    data: params,
  });
}

// PC游戏详情页store rank页签 获取子表格数据，即各个国家的排名数据
export const fetchPCStoreRankDetailsCountriesRanks = async (params: any): Promise<any> => {
  return http({
    url: '/api/v1/intelligence_pc/detail/pc/store_rank/market',
    method: 'post',
    data: params,
    rawData: true,
  });
};

// 通用游戏详情页store rank页签 获取子表格数据，即各个国家的排名数据
export const fetchCommonMarketCountCountriesRanks = async (params: any): Promise<any> => {
  return http({
    url: '/api/v1/intelligence_pc/detail/game/store_rank/market',
    method: 'post',
    data: params,
    rawData: true,
  });
};

// Console游戏详情页store rank页签 获取子表格数据，即各个国家的排名数据
export const fetchConsoleStoreRankDetailsCountriesRanks = (payload: {
  edition_id: string;
  source: string[];
  markets: string[];
  end_date: string;
}): Promise<any> => {
  return http({
    url: '/api/v1/intelligence_pc/detail/console/store_rank/market',
    method: 'post',
    data: payload,
    rawData: true,
  }) as Promise<any>;
};

// PC、Console游戏聚合详情页store rank页签 数据
export async function getPCConsoleStoreRanksDetailsData(
  params: IStoreRankDetailsDataFetchParams,
): Promise<{
    data: IStoreRankDetailsResData[];
    req: IStoreRankDetailsDataFetchParams;
  }> {
  return http({
    url: '/api/v1/intelligence_pc/pcConsoleGame/detail/store_ranks',
    method: 'post',
    data: params,
    rawData: true,
  }) as any;
}

// PC、Console游戏聚合详情页store rank页签-Rank Trend页面-宽表 数据
export async function getCombieStoreRanksDetailsRankTrendData(
  params: IStoreRankDetailsDataFetchParams,
): Promise<{
    data: IStoreRankDetailsResData[];
    req: IStoreRankDetailsDataFetchParams;
  }> {
  return http({
    url: '/api/v1/intelligence_pc/storeRank/pc_console',
    method: 'post',
    data: params,
    rawData: true,
  }) as any;
}

export async function fetchPCConsoleStoreRankDetailsCountriesRanks(params: any) {
  return http({
    url: '/api/v1/intelligence_pc/pcConsoleGame/detail/console/store_rank/market',
    method: 'post',
    data: params,
    rawData: true,
  }) as any;
}
